import { BodyM, Button } from '@casavo/base-ui'
import cn from 'classnames'
import { useField } from 'formik'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import React, { useEffect, useRef } from 'react'

import { useAuthentication } from '@/context/AuthProvider'
import { Toast } from '@/design-lib/components/Toast'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { theme } from '@/design-lib/theme'
import { gridCenter, marginBottom, marginRight } from '@/shared-styles/styles.css'
import { isLocaleEnabledFor } from '@/utils/locale-variables'

import Disclaimer from '../Disclaimer'
import { FormikCheckbox } from '../FormFields/FormikCheckbox'
import FormikInput from '../FormFields/FormikInput'
import Icon from '../Icon'
import Calendar from '../Icon/Calendar'
import Phone from '../Icon/Phone'
import ReadMoreToggle from '../ReadMoreToggle'

import { errorToast, maxWidthZero, warn } from './styles.css'

type RequestVisitFormProps = {
  isVisitRequestType: boolean
  onBack: VoidFunction
  showErrorMessage: boolean
  submitting: boolean
}

export const RequestContactForm: React.FC<RequestVisitFormProps> = ({
  isVisitRequestType,
  onBack,
  showErrorMessage,
  submitting,
}) => {
  const { lang, t } = useTranslation()
  const { user } = useAuthentication()
  const errorRef = useRef<HTMLDivElement>(null)
  const [_, __, { setValue: setNameValue }] = useField<string>('name')

  useEffect(() => {
    if (user) {
      const name = user?.first_name && user?.last_name ? `${user?.first_name} ${user?.last_name}` : ''
      setNameValue(name)
    }
  }, [user])

  return (
    <>
      <div className={marginBottom.s}>
        <FormikInput
          required
          id="name"
          label={t('contact-form:casavo.name')}
          name="name"
          type="text"
          onClick={() => navigator.userAgent.match(/android/i) && window.scrollTo(0, 0)}
        />
      </div>

      <div className={marginBottom.l}>
        <FormikInput
          required
          id="phoneNumber"
          inputMode="numeric"
          label={t('contact-form:phone')}
          name="phoneNumber"
          prefixName="phoneNumberPrefix"
          type="tel"
        />
      </div>

      <div className={marginBottom.s}>
        <FormikCheckbox
          center={false}
          id="marketing-communications"
          label={<Trans components={[<strong key="0" />]} i18nKey={'privacy:gdpr.marketing-consent-checkbox'} />}
          name="hasAcceptedMarketing"
          subText={<span>{t('privacy:gdpr.marketing-consent-full-text')}</span>}
        />
      </div>

      {isLocaleEnabledFor('consentsCasavoForm', lang) && (
        <FormikCheckbox
          center={false}
          className={marginRight['4xl']}
          id="consentForCasavoMortgageService"
          label={<Trans components={[<strong key="0" />]} i18nKey={'privacy:mortgage.title'} />}
          name="consentForCasavoMortgageService"
          subText={
            <ReadMoreToggle
              fullText={t('privacy:mortgage.full-text')}
              shortText={
                <Trans
                  components={[<span key="0" className={sprinkles({ textDecoration: 'underline' })} />]}
                  i18nKey={'privacy:mortgage.short-text'}
                />
              }
            />
          }
        />
      )}
      {showErrorMessage && (
        <div ref={errorRef} data-testid="error-message">
          <Toast className={errorToast} styleType="error" title="">
            <Trans components={[<br key="0" />]} i18nKey="listing-detail:request_visit_error" />
            {/* <Trans
              components={[<br key="0" />]}
              i18nKey={
                isEmailInvalid ? 'listing-detail:request_visit_email_error' : 'listing-detail:request_visit_error'
              }
            /> */}
          </Toast>
        </div>
      )}
      <div className={cn(warn)}>
        <Icon.PhoneSolid />
        <BodyM>
          <Trans
            components={[<strong key="0" style={{ color: theme.colors.warning700 }} />]}
            i18nKey={
              isVisitRequestType ? t('listing-detail:RequestVisit_Modal_Warn') : t('listing-detail:BookCall_Modal_Warn')
            }
          />
        </BodyM>
      </div>
      <Disclaimer
        className={sprinkles({ marginTop: 'm', paddingBottom: 'm' })}
        cta={isVisitRequestType ? t('listing-detail:request_visit') : t('listing-detail:request_call')}
      />

      <div className={cn(gridCenter, marginBottom.l, sprinkles({ marginTop: 'auto' }))}>
        <Button
          ariaLabel="back to previous step"
          className={maxWidthZero}
          Icon={() => <Icon.Chevron direction="left" />}
          styleVariant="tertiary"
          onClick={onBack}
        />

        <Button
          center
          data-testid="submit-request"
          disabled={submitting}
          Icon={(props) => (isVisitRequestType ? <Calendar {...props} /> : <Phone {...props} />)}
          label={isVisitRequestType ? t('listing-detail:request_visit') : t('listing-detail:request_call')}
          loading={submitting}
          minWidth={225}
          type="submit"
        />
      </div>
    </>
  )
}
